<template>
	<app-page>
		<app-heading title="Issues">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-row>
			<app-cell>
				<div>
					<app-button-confirm
						v-if="$ravello.can('system.post')"
						color="secondary"
						icon="fa-copy"
						small
						label-confirm="Resolve as Duplicate"
						:description="'Are you sure you want to resolve those issues as duplicate?'"
						title="Resolve as duplicate"
						@click="onResolveSelected('Duplicate')"
						:disabled="!selected || !selected.length"
					>
						Resolve as Duplicate
					</app-button-confirm>
					<app-button-confirm
							v-if="$ravello.can('system.post')"
							color="secondary"
							icon="fa-eraser"
							small
							label-confirm="Remove"
							:description="'Are you sure you want to resolve selected events without reason?'"
							title="Clean Issues"
							@click="onResolveSelected()"
							:disabled="!selected || !selected.length"
						>
							Clean
						</app-button-confirm>
				</div>
				<app-data-table
					:items="issues"
					:headers="headers"
					:total="count"
					:limit="100"
					:refresh.sync="refresh"
					select
					@update="fetchIssues"
					@select="updateSelection"
				>
					<template v-slot:ack="{ item }">
						<div class="success--text" v-if="item.ack">ACK</div>
						<div class="font-weight-bold warning--text" v-else>NEW</div>
					</template>
					<template v-slot:log="{ item }">
						<app-json
							v-if="typeof getParsedLog(item.log) === 'object'"
							:data="getParsedLog(item.log)"
						/>
						<div v-else>{{item.log}}</div>
					</template>
					<template v-slot:incidents="{ item }">
						<a @click="showIncidents(item.incidents)">{{ item.incidents.length }}</a>
					</template>
					<template v-slot:user="{ item }">
							<div v-if="item.ack">{{ getUserName(item.user) }}</div>
							<div v-else><i>Not Acknowledged</i></div>
					</template>
					<template v-slot:actions="{ item }">
							<app-button-confirm
							v-if="$ravello.can('system.post')"
							color="secondary"
							icon="fa-check"
							table
							label-confirm="Acknowledge"
							title="Acknowledge Issue"
							custom
							:disabled="item.ack"
							@click="onAcknowledge(item._id)"
							@close="name = ''"
						>
							<template v-slot:body>
								<app-dialog-body>
									<div>Are you sure you want to acknowledge issue <b>#{{item._id}}</b>?</div>
									<input-text v-model="name" label="Name" />
								</app-dialog-body>
							</template>
							Acknowledge
						</app-button-confirm>
						<app-button-confirm
							v-if="$ravello.can('system.post')"
							color="success"
							icon="fa-thumbs-up"
							table
							label-confirm="Resolve"
							title="Resolve Event"
							custom
							@click="onResolve(item._id)"
							@close="reason = ''"
						>
							<template v-slot:body>
								<app-dialog-body>
									<div>Resolving issue <b>{{item.name ? item.name : `#${item._id}`}}</b></div>
									<input-text v-model="reason" label="Reason" />
								</app-dialog-body>
							</template>
							Resolve
						</app-button-confirm>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>

		<app-dialog
			v-model="incidents.show"
			max-width="600"
			height="400"
			max-height="400"
			title="Incidents"
		>
			<app-dialog-body>
				<app-json :data="incidents.content" open />
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat="flat"
					@click="incidents.show = false"
				>Close</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
export default {
	data() {
		return {
			issues: [],
			usersMap: undefined,
			incidents: {
				show: false,
				content: [],
			},
			reason: '',
			selected: [],
			name: '',
			count: -1,
			refresh: false,
			headers: [{
				text: 'Status',
				value: 'ack',
				sortable: false,
			}, {
				text: 'Name',
				value: '_id',
				sortable: false,
				cb: item => item.name || item._id,
			}, {
				text: 'Type',
				value: 'type',
				sortable: false,
			}, {
				text: 'Source',
				value: 'source',
				sortable: false,
			}, {
				text: 'Log',
				value: 'log',
				sortable: false,
			}, {
				text: 'Incidents',
				value: 'incidents',
				sortable: false,
			}, {
				text: 'Ackowledged by',
				value: 'user',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	methods: {
		getParsedLog(log) {
			if (!log) { return null }
			if (log.substr(0, 1) === '{' || log.substr(0, 1) === '[') {
				return JSON.parse(log)
			}

			return log
		},
		getUserName(id) {
			if (!this.usersMap[id]) {
				return 'Undefined'
			}

			return this.usersMap[id]
		},
		async showIncidents(incidents) {
			this.incidents.show = true
			this.incidents.content = incidents
		},
		updateSelection(selected) {
			this.selected = selected
		},

		async fetchUsers() {
			if (this.usersMap) { return }

			const request = this.$ravello.request()
				.v(3)
				.to('users')

			try {
				const users = await request.dataSource()
				this.usersMap = users.reduce((acc, user) => ({ ...acc, [user.value]: user.text }), {})
			} catch (err) {
				this.usersMap = {}
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchIssues(pagination) {
			await this.fetchUsers()
			const request = this.$ravello.request()
				.to('system')
				.to('issues')
				.fromPagination(pagination)

			try {
				this.issues = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async onAcknowledge(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('issues')
					.to(id)
					.to('ack')
					.create(this.name !== '' ? { name: this.name } : {})
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
		async resolve(id, reason = '') {
			try {
				await this.$ravello.request()
					.to('system')
					.to('issues')
					.to(id)
					.to('resolve')
					.create(reason !== '' ? { reason } : {})
				return true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			return false
		},
		async onResolve(id) {
			this.$store.dispatch('app/startLoading')
			const success = await this.resolve(id, this.reason)
			if (success) {
				this.refresh = true
			}
			this.$store.dispatch('app/stopLoading')
		},
		async onResolveSelected(reason = '') {
			this.$store.dispatch('app/startLoading')
			await Promise.all(this.selected.map(id => this.resolve(id, reason)))
			this.refresh = true
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
